import { ArrowLeftIcon, ArrowLineDownIcon, ArrowRightIcon, CheckIcon } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GlobalSidebarItem from '../components/sidebar/GlobalSidebar/GlobalSidebarItem';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usePackStorage } from '../Layout/usePackStorage';
import AttemptAnalysis from './components/AttemptAnalysis';
import DifficultyAnalysis from './components/DifficultyAnalysis';
import DownloadAnalysisModal from './components/DownloadAnalysisModal';
import Overview from './components/Overview';
import PerformanceAnalysis from './components/PerformanceAnalysis';
import QuestionByQuestionAnalysis from './components/QuestionByQuestionAnalysis';
import QuestionJourney from './components/QuestionJourney';
import ScorePotential from './components/ScorePotential';
import SubjectMovement from './components/SubjectMovement';
import TimeAnalysis from './components/TimeAnalysis';
import PremiumFeatureModal from './PremiumFeatureModal';

// Mock data imports
import {
  AllQuestionsAnalysisMockData,
  attemptAnalysisMockData,
  ComparativeAnalysisMockData,
  difficultyAnalysisMockData,
  overviewMockData,
  PercentileMockData,
  performanceAnalysisMockData,
  QuestionJourneyMockData,
  subjectMovementTimelineMockData,
  testMockData,
  timeAnalysisMockData,
} from './MockData.js';

const tabDescriptionMapping = {
  Overview: 'This section helps you understand your performance in the test attempts.',
  'Performance Analysis':
    'This section helps you quickly analyse your performance measured in terms of attempts that were correct, incorrect, unattempted and questions that were not visited at all.',
  'Score Potential':
    'This section categories the question(s) you got wrong in the test into respective chapters & topics so that you can analyze your mistakes and work on them.',
  'Attempt Analysis': "Know how you're solving questions in your attempted test. Go through the definitions first.",
  'Time Analysis':
    'Time matters in competitive exams. This section shows time spent, attempt rate, and accuracy per subject—helping you balance speed and precision.',
  'Difficulty Analysis':
    'Every exam has Easy, Moderate, and Tough questions. This section shows your accuracy in each, helping you focus on getting the easy and moderate ones right.',
  'Subject Movement':
    'This displays your question attempts for a specific subject within a given time frame. Multiple attempts at the same question will be counted separately if you revisit and change your answer for particular qs in that subject.',
  'Question Journey': 'This section shows how you moved through question during the test for 3 hours',
  'Qs by Qs Analysis': 'This gives you quick snapshot of your attempts subject-wise as well as question-type wise.',
};

const steps = [
  {
    target: '.comparative-target',
    content: 'Click here to view comparative analysis and compare your performance with other students.',
    title: 'View Comparative Analysis',
    disableBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '.detailed-analysis-target',
    content: 'Click here to view different types of analysis to evaluate your performance better.',
    title: 'Detailed Analysis',
    placement: 'right',
  },
  {
    target: '.download-analysis-target',
    content: 'Click here to download the "Detailed Analysis" or "Quizrr AI Analysis".',
    title: 'Download Analysis',
    spotlightPadding: 5,
  },
];

export default function TestAnalysisDemo() {
  const { packId, testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies();
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalProps, setModalProps] = useState({});

  const user = JSON.parse(localStorage.getItem('userProfile') || '{}');
  const headers = sessionStorage.getItem('token') ? { Authorization: `Bearer ${sessionStorage.getItem('token')}` } : {};

  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  const examType = 'jee';

  // tour guide states
  const [stepIndex, setStepIndex] = useState(0);
  const [showTorchGuide, setShowTorchGuide] = useState(true);

  // loading state
  const [isLoading, setIsLoading] = useState(false);

  // test states - using mock data directly
  const [test, setTest] = useState(testMockData);
  const [totalMarks, setTotalMarks] = useState(null);

  // download analysis state
  const [downloadingAnalysisDisable, setdownloadingAnalysisDisable] = useState(false);
  const [aiAnalysisAvailable, setAiAnalysisAvailable] = useState(false);
  const [showDownloadAnalysisModal, setShowDownloadAnalysisModal] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [premiumFeatureName, setPremiumFeatureName] = useState('');

  // state for comparative and personal analysis
  const [hideComparativeAnalysis, setHideComparativeAnalysis] = useState(false);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('personal');

  // state for selected tab
  const [tabsToShow, setTabsToShow] = useState(overviewMockData?.data?.tabsToShow || []);
  const [tabIconsMapping, setTabIconsMapping] = useState(overviewMockData?.data?.tabIconsMapping || {});
  const [selectedTab, setSelectedTab] = useState((overviewMockData?.data?.tabsToShow || [])[0]);

  // check if analysis is available
  const [detailedAnalysisAvailable, setDetailedAnalysisAvailable] = useState(true);
  const [showAnalysis, setShowAnalysis] = useState(true);

  // analysis states
  const [percentile, setPercentile] = useState(PercentileMockData);
  const [overviewData, setOverviewData] = useState(overviewMockData?.data);
  const [performanceAnalysisData, setPerformanceAnalysisData] = useState(performanceAnalysisMockData);
  const [comparativeData, setComparativeData] = useState(ComparativeAnalysisMockData);
  const [paidFeatures, setPaidFeatures] = useState(overviewMockData?.data?.paidFeatures || {});

  // Get category ID from URL params or location state
  const categoryId = searchParams.get('categoryId') || location.state?.selectedCategoryId;

  // Scroll states for hide-on-scroll behavior
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const html = document.documentElement;
    let previousClass = '';

    // Check and store the existing theme class
    if (html.classList.contains('dark')) {
      previousClass = 'dark';
    } else if (html.classList.contains('light')) {
      previousClass = 'light';
    }

    html.classList.remove('dark', 'light');
    html.classList.add('light');

    return () => {
      html.classList.remove('light');
      if (previousClass) {
        html.classList.add(previousClass);
      }
    };
  }, []);

  // Function to handle back navigation
  const handleBackNavigation = () => {
    navigate('/');
  };

  // Function to show premium feature modal
  const showPremiumFeatureModal = (featureName) => {
    setPremiumFeatureName(featureName);
    setShowPremiumModal(true);
  };

  // Cycle text every 2 seconds when AI analysis is available
  useEffect(() => {
    if (!aiAnalysisAvailable) return;

    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % 2);
    }, 4000);

    return () => clearInterval(interval);
  }, [aiAnalysisAvailable]);

  useEffect(() => {
    const checkIfTablet = () => {
      setShowTorchGuide(window.innerWidth > 1024);
    };

    checkIfTablet();
    window.addEventListener('resize', checkIfTablet);
    return () => window.removeEventListener('resize', checkIfTablet);
  }, []);

  // Scroll handler for hide-on-scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024;

      if (!isTabletOrBelow) {
        // Always show header on desktop
        setIsHeaderVisible(true);
        return;
      }

      // Hide/show logic for tablet and mobile
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  // Initialize data from mock
  useEffect(() => {
    // Set initial data from mock
    if (testMockData && testMockData.subjects) {
      const marks = {
        overall: testMockData?.totalMarks,
        ...testMockData?.subjects.reduce((acc, subject) => {
          acc[subject.subjectId._id] = subject.totalMarks;
          return acc;
        }, {}),
      };
      setTotalMarks(marks);
    }

    const breadcrumbItems = [
      {
        title: 'JEE Demo',
        onClick: handleBackNavigation,
      },
      {
        title: testMockData?.title || 'Demo Test',
      },
      {
        title: 'Analysis',
      },
      {
        title: 'Overview',
      },
    ];
    setBreadcrumbItems(breadcrumbItems);

    // Set AI analysis available based on mock data
    if (testMockData?.basicAnalysis?.aiReportGenerated) {
      setAiAnalysisAvailable(true);
    }

    // Set comparative analysis availability
    setHideComparativeAnalysis(overviewMockData?.data?.hideComparative || false);

    // Track demo page view
    if (window.webengage) {
      window.webengage.track('Viewed Demo Analysis', {
        'User Id': user?.email,
        'Exam Type': 'JEE',
        'Test Name': testMockData?.title,
        Source: 'Demo Page',
      });
    }
  }, []);

  const handleViewSolutions = () => {
    setModalProps({
      title: 'This is How Toppers Improve 🏆',
      body: "Toppers don't guess — they analyze.",
      desc: 'Enroll in Quizrr Test Series to access detailed solutions and improve after every test.',
      ctaText: 'Unlock Solutions with Quizrr Test Series',
      onCtaClick: () =>
        window.open(
          'https://www.quizrr.in/jee-main-test-series?utm_source=demo_analysis&utm_medium=solutions&utm_campaign=demo_analysis_sale',
          '_blank',
        ),
    });
    setShowPremiumModal(true);
  };

  const handleDownloadAnalysis = () => {
    setModalProps({
      title: 'Unlock Detailed Analysis ',
      body: 'Get in-depth insights into your performance.',
      desc: 'Enroll in Quizrr Test Series to download comprehensive analysis reports.',
      ctaText: 'Download with Quizrr Test Series',
      onCtaClick: () =>
        window.open(
          'https://www.quizrr.in/jee-main-test-series?utm_source=demo_analysis&utm_medium=download_analysis&utm_campaign=demo_analysis_sale',
          '_blank',
        ),
    });
    setShowPremiumModal(true);
  };

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    // update title of last breadcrumb item
    setBreadcrumbItems((prevItems) => {
      const updated = [...prevItems];
      if (updated.length > 3) {
        updated[3] = { ...updated[3], title: tab };
      }
      return updated;
    });
  };

  return (
    // <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
    <div className="bg-[var(--q3-surface-default)]">
      <div className="min-[1250px]:hidden q3-glass fixed border-t left-0 right-0 lg:left-[102px] bottom-0 z-20 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] p-4">
        <div className="flex flex-row">
          {!hideComparativeAnalysis && (
            <div className="flex flex-row">
              <Select
                value={activeAnalysisTab === 'comparative' ? 'Comparative' : 'Personal'}
                onValueChange={(val) => {
                  if (val.toLowerCase() === 'comparative') {
                    setSelectedTab('Performance Analysis');
                  } else {
                    setSelectedTab(tabsToShow?.[0]);
                  }
                  setActiveAnalysisTab(val.toLowerCase());
                }}
              >
                <SelectTrigger
                  className={`rounded-full flex items-center gap-2 px-4 py-2 bg-[var(--q3-surface-default)] text-[var(--q3-accent-normal)] border border-[var(--q3-stroke-normal)] hover:bg-[var(--q3-surface-dimmest)] transition-colors w-auto cursor-pointer [&>svg]:text-[var(--q3-accent-normal)]`}
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="rounded-md border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] shadow-md max-h-64 overflow-y-auto cursor-pointer">
                  <SelectGroup>
                    {[
                      {
                        key: 'personal',
                        value: 'Personal',
                      },
                      {
                        key: 'comparative',
                        value: 'Comparative',
                      },
                    ].map((item) => (
                      <SelectItem key={item.key} value={item.value}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <div className="border-r ml-[11px]" />
            </div>
          )}
          <div className="flex flex-row gap-[11px] ml-[11px] overflow-x-auto">
            {activeAnalysisTab === 'personal'
              ? tabsToShow?.map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })
              : ['Performance Analysis', 'Attempt Analysis', 'Time Analysis', 'Difficulty Analysis'].map((tab) => {
                  return (
                    <Button
                      variant={'outline'}
                      className={`rounded-full ${selectedTab === tab ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                      onClick={() => handleTabChange(tab)}
                      key={`tab-${tab}`}
                    >
                      {tab}
                      {selectedTab === tab && <CheckIcon />}
                    </Button>
                  );
                })}
          </div>
        </div>
      </div>

      <div className="flex flex-row max-w-[1300px] mx-auto flex-1 m-0 p-0 overflow-hidden">
        {/* Side Section */}
        <div className="hidden min-[1250px]:flex flex-col w-[264px] items-start pl-6">
          <div className="py-6 px-4 flex flex-col items-start">
            {/* <div
              className="text-[var(--q3-neutral-default)] cursor-pointer bg-[var(--q3-surface-dimmer)] rounded-full p-2"
              onClick={handleBackNavigation}
            >
              <ArrowLeftIcon size={24} />
            </div> */}
            <div className="text-[var(--q3-neutral-default)] text-2xl font-bold my-2">Test Analysis</div>
            <div className="text-sm text-[var(--q3-neutral-light)]">{test?.title}</div>
          </div>
          <div className="w-full">
            {!hideComparativeAnalysis ? (
              <Tabs
                defaultValue="personal"
                onValueChange={(value) => {
                  if (value.toLowerCase() === 'comparative') {
                    setSelectedTab('Performance Analysis');
                  } else {
                    setSelectedTab(tabsToShow?.[0]);
                  }
                  setActiveAnalysisTab(value);
                }}
              >
                <TabsList className="w-full mb-2">
                  <TabsTrigger value="personal">
                    Personal {activeAnalysisTab === 'personal' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                  <TabsTrigger value="comparative" className="pl-3 pr-2 py-2 comparative-target">
                    Comparative {activeAnalysisTab === 'comparative' && <CheckIcon size={20} weight="bold" />}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="personal" className={'detailed-analysis-target'}>
                  {tabsToShow?.map((tab, index) => (
                    <GlobalSidebarItem
                      key={index}
                      icon={tabIconsMapping?.[tab]}
                      title={tab}
                      isSelected={tab === selectedTab}
                      onClick={() => handleTabChange(tab)}
                      className={`w-full`}
                    />
                  ))}
                </TabsContent>
                <TabsContent value="comparative">
                  {['Performance Analysis', 'Attempt Analysis', 'Time Analysis', 'Difficulty Analysis'].map(
                    (tab, index) => (
                      <GlobalSidebarItem
                        key={index}
                        icon={tabIconsMapping?.[tab]}
                        title={tab}
                        isSelected={tab === selectedTab}
                        onClick={() => handleTabChange(tab)}
                        className="w-full"
                      />
                    ),
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              <div>
                {tabsToShow?.map((tab, index) => (
                  <GlobalSidebarItem
                    key={index}
                    icon={tabIconsMapping?.[tab]}
                    title={tab}
                    isSelected={tab === selectedTab}
                    onClick={() => handleTabChange(tab)}
                    className="w-full"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col w-full h-screen overflow-y-auto" ref={scrollContainerRef}>
          <div
            className={`z-10 flex flex-col lg:flex-row justify-between p-6 pb-4 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
              isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
            }`}
          >
            <div className="hidden lg:flex gap-2 flex-col items-start">
              <p className="text-2xl text-[var(--q3-neutral-default)] font-bold whitespace-nowrap">{selectedTab}</p>
            </div>
            <div className="flex w-full items-start justify-between">
              <div
                className="block lg:hidden cursor-pointer text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dimmer)] rounded-full p-2"
                onClick={handleBackNavigation}
              >
                <ArrowLeftIcon size={24} />
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  className="group relative inline-flex h-10 overflow-hidden rounded-full p-[1px] focus:outline-none download-analysis-target"
                  onClick={handleDownloadAnalysis}
                >
                  <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#E2CBFF_0%,#393BB2_50%,#E2CBFF_100%)]" />
                  <span className="relative inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-[var(--q3-surface-dim)] px-3 py-1 text-sm font-medium text-[var(--q3-neutral-default)] backdrop-blur-3xl overflow-hidden">
                    <ArrowLineDownIcon
                      size={22}
                      className="transition-transform duration-300 group-hover:translate-y-0.5"
                    />
                    <div className="hidden lg:block relative ml-2 h-[1.25rem] overflow-hidden">
                      <div
                        className={`transition-transform duration-500 ease-in-out ${
                          aiAnalysisAvailable
                            ? currentTextIndex === 0
                              ? 'translate-y-0'
                              : '-translate-y-[1.25rem]'
                            : 'translate-y-0'
                        }`}
                      >
                        <p className="h-[1.25rem] flex items-center">Download Analysis</p>
                        <p className="h-[1.25rem] flex items-center">AI Report Available</p>
                      </div>
                    </div>
                  </span>
                </Button>

                <Button
                  className={
                    'bg-[var(--q3-accent-normal)] rounded-full h-10 flex flex-row items-center justify-center transition-colors duration-300 hover:bg-[var(--q3-accent-dark)] group'
                  }
                  onClick={handleViewSolutions}
                >
                  <p className="pl-2">View Solution</p>
                  <ArrowRightIcon size={22} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>

            <div className="lg:hidden mt-2 flex flex-col gap-2 items-start">
              <div>
                <p className="text-2xl text-[var(--q3-neutral-default)] font-bold">{selectedTab}</p>
              </div>
            </div>
          </div>
          <div className="p-6 pt-4 pb-28">
            {selectedTab === 'Overview' ? (
              <Overview
                overviewData={overviewData}
                percentile={percentile}
                analysisVideo={test?.analysisVideo}
                notes={test?.basicAnalysis?.notes}
                testTitle={test?.title}
                packId={packId}
                testId={testId}
                isDemo={true}
              />
            ) : selectedTab === 'Performance Analysis' ? (
              <PerformanceAnalysis
                isDemo={true}
                performanceData={performanceAnalysisData}
                comparativeData={comparativeData}
                isComparative={activeAnalysisTab === 'comparative'}
                totalMarks={totalMarks}
              />
            ) : selectedTab === 'Score Potential' ? (
              <ScorePotential packId={packId} testId={testId} isDemo={true} />
            ) : selectedTab === 'Attempt Analysis' ? (
              <AttemptAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                attemptAnalysisData={attemptAnalysisMockData}
                packId={packId}
                testId={testId}
                isDemo={true}
              />
            ) : selectedTab === 'Time Analysis' ? (
              <TimeAnalysis
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                timeAnalysisData={timeAnalysisMockData}
                packId={packId}
                testId={testId}
                totalTime={test?.totalTime}
                isDemo={true}
              />
            ) : selectedTab === 'Difficulty Analysis' ? (
              <DifficultyAnalysis
                packId={packId}
                testId={testId}
                difficultyAnalysisData={difficultyAnalysisMockData}
                isComparative={activeAnalysisTab === 'comparative'}
                comparativeData={comparativeData}
                isDemo={true}
              />
            ) : selectedTab === 'Subject Movement' ? (
              <div className="overflow-x-auto">
                <SubjectMovement
                  subjectMovementData={subjectMovementTimelineMockData}
                  packId={packId}
                  testId={testId}
                  isDemo={true}
                />
              </div>
            ) : selectedTab === 'Question Journey' ? (
              <QuestionJourney
                questionJourneyData={QuestionJourneyMockData}
                packId={packId}
                testId={testId}
                totalTime={test?.totalTime}
                isDemo={true}
              />
            ) : selectedTab === 'Qs by Qs Analysis' ? (
              <QuestionByQuestionAnalysis
                packId={packId}
                testId={testId}
                isDemo={true}
                AllQuestionsAnalysisData={AllQuestionsAnalysisMockData}
              />
            ) : (
              <></>
            )}
          </div>
        </div>

        {/* Premium Feature Modal */}
        {showPremiumModal && (
          <PremiumFeatureModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} {...modalProps} />
        )}

        {showDownloadAnalysisModal && (
          <DownloadAnalysisModal
            isOpen={showDownloadAnalysisModal}
            onClose={() => setShowDownloadAnalysisModal(false)}
            packId={packId}
            testId={testId}
            testTitle={test?.title}
            userId={user._id}
            userEmail={user?.email}
            userToken={cookies.token}
            setdownloadingAnalysisDisable={setdownloadingAnalysisDisable}
            isDemo={true}
          />
        )}
      </div>
    </div>

    // </AppLayout>
  );
}
